import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
  Link,
} from "@material-ui/core";
import {
  LockOutlined,
  MailOutline,
  Visibility,
  VisibilityOff,
  WhatsApp,
  EventAvailable,
  AutorenewRounded,
  CheckCircle,
} from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import LupaRH from "../../components/LupaRH";
import { ondasMar } from "../../utils/estiloNavy";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

// Paleta do I.A Recursos Humanos: verde-água.
const VIOLETA = "#0d9488";
const VIOLETA_CLARO = "#14b8a6";
const VIOLETA_ESCURO = "#06302d";
const AMBAR = "#5eead4";

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  return {
    root: { minHeight: "100vh" },

    /* ---------- painel visual (esquerda) ---------- */
    painel: {
      position: "relative",
      overflow: "hidden",
      color: "#fff",
      backgroundColor: VIOLETA_ESCURO,
      backgroundImage:
        "radial-gradient(700px 420px at 0% 0%, rgba(45,212,191,0.35), transparent 60%)," +
        "radial-gradient(520px 380px at 100% 100%, rgba(94,234,212,0.14), transparent 65%)," +
        "linear-gradient(160deg, #0b4440 0%, #06302d 55%, #04201e 100%)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: theme.spacing(6, 7),
      ...ondasMar("rgba(94,234,212,0.08)", "rgba(94,234,212,0.12)"),
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(5, 4) },
    },
    // grade pontilhada sutil ao fundo
    grade: {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundImage: "radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px)",
      backgroundSize: "26px 26px",
      maskImage: "linear-gradient(180deg, rgba(0,0,0,0.9), transparent 85%)",
      WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,0.9), transparent 85%)",
    },
    painelTopo: { position: "relative", zIndex: 1 },
    selo: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 14px",
      borderRadius: 999,
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.14)",
      fontSize: "0.8rem",
      fontWeight: 600,
      letterSpacing: 0.4,
      color: "#e0fbf6",
    },
    seloPonto: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: AMBAR,
      boxShadow: `0 0 0 0 ${AMBAR}`,
      animation: "$pulsar 2s ease-out infinite",
    },
    manchete: {
      marginTop: theme.spacing(3),
      fontSize: "2.9rem",
      fontWeight: 800,
      lineHeight: 1.08,
      letterSpacing: -0.5,
      maxWidth: 560,
      [theme.breakpoints.down("md")]: { fontSize: "2.3rem" },
    },
    mancheteDestaque: {
      background: `linear-gradient(90deg, ${AMBAR}, #ccfbf1)`,
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      color: "transparent",
    },
    subManchete: {
      marginTop: theme.spacing(2),
      color: "#b5e6df",
      fontSize: "1.05rem",
      maxWidth: 480,
      lineHeight: 1.5,
    },

    /* quadro de etapas do recrutamento */
    quadro: {
      position: "relative",
      zIndex: 1,
      marginTop: theme.spacing(5),
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 14,
      maxWidth: 600,
    },
    coluna: {
      borderRadius: 16,
      padding: 12,
      minHeight: 230,
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.10)",
    },
    colunaTitulo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: "0.72rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 1,
      color: "#9ad9d0",
      marginBottom: 10,
    },
    contador: {
      fontSize: "0.7rem",
      padding: "1px 8px",
      borderRadius: 999,
      background: "rgba(255,255,255,0.1)",
      color: "#fff",
    },
    cartao: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 10px",
      borderRadius: 12,
      background: "rgba(255,255,255,0.92)",
      color: "#0f2a28",
      marginBottom: 8,
      boxShadow: "0 6px 16px rgba(2,20,18,0.3)",
      animation: "$entrar .6s ease backwards",
    },
    avatar: {
      flexShrink: 0,
      width: 30,
      height: 30,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "0.75rem",
      fontWeight: 800,
      color: "#fff",
    },
    cartaoNome: { fontSize: "0.8rem", fontWeight: 700, lineHeight: 1.2 },
    cartaoInfo: { fontSize: "0.68rem", color: "#4f6f6b", lineHeight: 1.2 },
    // cartão que "anda" pelas etapas
    cartaoViajante: {
      position: "absolute",
      top: 172,
      left: 12,
      width: "calc((100% - 28px) / 3 - 24px)",
      border: `2px solid ${AMBAR}`,
      background: "#fff",
      boxShadow: "0 12px 28px rgba(45,212,191,0.4)",
      animation: "$viajar 9s cubic-bezier(.65,0,.35,1) infinite",
      zIndex: 2,
    },
    iconeIa: {
      fontSize: 16,
      color: VIOLETA,
      animation: "$girar 1.6s linear infinite",
    },

    rodape: {
      position: "relative",
      zIndex: 1,
      display: "flex",
      flexWrap: "wrap",
      gap: 22,
      marginTop: theme.spacing(5),
      color: "#c8eee8",
      fontSize: "0.88rem",
    },
    rodapeItem: { display: "flex", alignItems: "center", gap: 8 },
    rodapeIcone: { fontSize: 20, color: AMBAR },

    /* ---------- formulário (direita) ---------- */
    lado: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(4, 3),
      backgroundColor: escuro ? "#071a19" : "#f6fbfa",
    },
    form: {
      width: "100%",
      maxWidth: 400,
      animation: "$entrar .7s ease backwards",
    },
    marca: { display: "flex", alignItems: "center", gap: 14, marginBottom: theme.spacing(6) },
    marcaLogo: {
      width: 54,
      height: 54,
      flexShrink: 0,
      filter: "drop-shadow(0 6px 12px rgba(13,148,136,0.25))",
    },
    marcaIa: {
      display: "block",
      fontSize: "1.5rem",
      fontWeight: 900,
      letterSpacing: 1,
      color: VIOLETA,
      lineHeight: 1,
    },
    marcaNome: {
      display: "block",
      fontSize: "0.95rem",
      fontWeight: 600,
      color: theme.palette.text.secondary,
      letterSpacing: 0.3,
      marginTop: 4,
    },
    sobretitulo: {
      fontSize: "0.78rem",
      fontWeight: 700,
      letterSpacing: 1.5,
      textTransform: "uppercase",
      color: VIOLETA_CLARO,
    },
    titulo: {
      fontSize: "2.2rem",
      fontWeight: 800,
      lineHeight: 1.15,
      marginTop: 6,
      color: theme.palette.text.primary,
    },
    subtitulo: {
      color: theme.palette.text.secondary,
      marginTop: 8,
      marginBottom: theme.spacing(4),
    },
    rotulo: {
      fontWeight: 600,
      fontSize: "0.85rem",
      color: theme.palette.text.primary,
      marginBottom: -6,
    },
    input: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 14,
        backgroundColor: escuro ? "rgba(255,255,255,0.04)" : "#fff",
        transition: "box-shadow .2s ease",
        "& fieldset": { borderColor: escuro ? "rgba(255,255,255,0.14)" : "#d5e8e5" },
        "&:hover fieldset": { borderColor: VIOLETA_CLARO },
        "&.Mui-focused fieldset": { borderColor: VIOLETA, borderWidth: 2 },
        "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(20,184,166,0.18)" },
      },
      "& input:-webkit-autofill": {
        WebkitBoxShadow: `0 0 0 1000px ${escuro ? "#0e2725" : "#fff"} inset !important`,
        WebkitTextFillColor: `${escuro ? "#e9fbf8" : "#0f2a28"} !important`,
        animationName: "$autofill",
        animationDuration: "0.001s",
      },
    },
    icone: { color: VIOLETA_CLARO },
    botao: {
      marginTop: theme.spacing(4),
      padding: "14px 0",
      borderRadius: 14,
      textTransform: "none",
      fontSize: "1rem",
      fontWeight: 700,
      color: "#fff",
      background: `linear-gradient(135deg, ${VIOLETA} 0%, ${VIOLETA_CLARO} 100%)`,
      boxShadow: "0 12px 26px rgba(13,148,136,0.35)",
      transition: "transform .2s ease, box-shadow .2s ease",
      "&:hover": {
        background: `linear-gradient(135deg, #0f766e 0%, ${VIOLETA} 100%)`,
        transform: "translateY(-2px)",
        boxShadow: "0 16px 32px rgba(13,148,136,0.45)",
      },
    },
    cadastro: {
      marginTop: theme.spacing(3),
      textAlign: "center",
      color: theme.palette.text.secondary,
      fontSize: "0.9rem",
    },
    cadastroLink: { color: VIOLETA, fontWeight: 700 },

    "@keyframes autofill": { from: { opacity: 1 }, to: { opacity: 1 } },
    "@keyframes entrar": {
      from: { opacity: 0, transform: "translateY(14px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    "@keyframes pulsar": {
      "0%": { boxShadow: "0 0 0 0 rgba(94,234,212,0.7)" },
      "100%": { boxShadow: "0 0 0 10px rgba(94,234,212,0)" },
    },
    "@keyframes girar": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    // percorre Triagem -> Entrevista -> Aprovado e volta
    "@keyframes viajar": {
      "0%, 22%": { transform: "translateX(0)", opacity: 1 },
      "30%, 55%": { transform: "translateX(calc(100% + 38px))", opacity: 1 },
      "63%, 88%": { transform: "translateX(calc(200% + 76px))", opacity: 1 },
      "94%": { transform: "translateX(calc(200% + 76px))", opacity: 0 },
      "95%": { transform: "translateX(0)", opacity: 0 },
      "100%": { transform: "translateX(0)", opacity: 1 },
    },
  };
});

const etapas = [
  {
    titulo: "Triagem",
    total: 12,
    cartoes: [
      { ini: "MC", cor: "#8b5cf6", nome: "Marina C.", info: "Atendente" },
      { ini: "RF", cor: "#ec4899", nome: "Rafael F.", info: "Vendedor" },
    ],
  },
  {
    titulo: "Entrevista",
    total: 5,
    cartoes: [
      { ini: "JL", cor: "#0ea5e9", nome: "Júlia L.", info: "Ter · 14h" },
      { ini: "TS", cor: "#f97316", nome: "Thiago S.", info: "Qua · 10h" },
    ],
  },
  {
    titulo: "Aprovado",
    total: 3,
    cartoes: [{ ini: "BA", cor: "#14b8a6", nome: "Beatriz A.", info: "Contratada" }],
  },
];

const Login = () => {
  const classes = useStyles();
  const escuro = useTheme().palette.type === "dark";
  const { handleLogin } = useContext(AuthContext);

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);
  const [hasSavedLogin, setHasSavedLogin] = useState(false);

  // detecta quando o Chrome preenche o login salvo
  const handleAnimationStart = (e) => {
    try {
      if (e && e.target && e.target.matches(":-webkit-autofill")) {
        setHasSavedLogin(true);
      }
    } catch (err) {}
  };

  useEffect(() => {
    const fetchSetting = async () => {
      try {
        const { data } = await api.get("/public-settings/userCreation");
        setAllowSignup(data.value === "enabled");
      } catch (err) {
        console.error("Error fetching userCreation setting", err);
      }
    };
    fetchSetting();
  }, []);

  const [erroLogin, setErroLogin] = useState("");
  const [entrando, setEntrando] = useState(false);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
    if (erroLogin) setErroLogin("");
  };

  const handlSubmit = async (e) => {
    e.preventDefault();
    if (!String(user.email || "").trim() || !String(user.password || "").trim()) {
      setErroLogin("Preencha o e-mail e a senha.");
      return;
    }
    setErroLogin("");
    setEntrando(true);
    const r = await handleLogin(user);
    setEntrando(false);
    if (r && r.erro) setErroLogin(r.erro);
  };

  return (
    <Grid container className={classes.root}>
      <CssBaseline />

      {/* painel visual */}
      <Grid item xs={false} sm={5} md={7} className={classes.painel}>
        <div className={classes.grade} />

        <div className={classes.painelTopo}>
          <span className={classes.selo}>
            <span className={classes.seloPonto} />
            Recrutadora IA online
          </span>
          <Typography component="h2" className={classes.manchete} translate="no">
            I.A <span className={classes.mancheteDestaque}>Recursos Humanos</span>
          </Typography>
          <Typography className={classes.subManchete}>
            Do primeiro “oi” no WhatsApp até a entrevista marcada — a IA faz a
            triagem e o seu time só entra na hora certa.
          </Typography>

          <div className={classes.quadro}>
            {etapas.map((et, i) => (
              <div key={et.titulo} className={classes.coluna}>
                <div className={classes.colunaTitulo}>
                  {et.titulo}
                  <span className={classes.contador}>{et.total}</span>
                </div>
                {et.cartoes.map((c, j) => (
                  <div
                    key={c.nome}
                    className={classes.cartao}
                    style={{ animationDelay: `${0.2 + i * 0.15 + j * 0.1}s` }}
                  >
                    <span className={classes.avatar} style={{ background: c.cor }}>
                      {c.ini}
                    </span>
                    <div>
                      <div className={classes.cartaoNome}>{c.nome}</div>
                      <div className={classes.cartaoInfo}>{c.info}</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* candidato novo passando pelas etapas */}
            <div className={`${classes.cartao} ${classes.cartaoViajante}`}>
              <span className={classes.avatar} style={{ background: AMBAR, color: "#053b36" }}>
                NC
              </span>
              <div>
                <div className={classes.cartaoNome}>Novo candidato</div>
                <div className={classes.cartaoInfo} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <AutorenewRounded className={classes.iconeIa} /> IA avaliando
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.rodape}>
          <span className={classes.rodapeItem}>
            <WhatsApp className={classes.rodapeIcone} /> Atendimento 24h
          </span>
          <span className={classes.rodapeItem}>
            <CheckCircle className={classes.rodapeIcone} /> Triagem automática
          </span>
          <span className={classes.rodapeItem}>
            <EventAvailable className={classes.rodapeIcone} /> Agenda de entrevistas
          </span>
        </div>
      </Grid>

      {/* formulário */}
      <Grid item xs={12} sm={7} md={5} className={classes.lado}>
        <form className={classes.form} noValidate onSubmit={handlSubmit}>
          <div className={classes.marca}>
            <LupaRH className={classes.marcaLogo} cor={escuro ? "#2dd4bf" : "#0f2a28"} />
            <div translate="no">
              <span className={classes.marcaIa}>I.A</span>
              <span className={classes.marcaNome}>Recursos Humanos</span>
            </div>
          </div>

          <Typography className={classes.sobretitulo}>Acesso ao painel</Typography>
          <Typography component="h1" className={classes.titulo}>
            {hasSavedLogin ? "Bem-vindo de volta" : "Entrar na sua conta"}
          </Typography>
          <Typography className={classes.subtitulo}>
            Use o e-mail e a senha cadastrados pelo seu administrador.
          </Typography>

          <Typography className={classes.rotulo}>E-mail</Typography>
          <TextField
            className={classes.input}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            name="email"
            placeholder="seuemail@empresa.com"
            value={user.email}
            onChange={handleChangeInput}
            autoComplete="email"
            autoFocus
            inputProps={{ onAnimationStart: handleAnimationStart }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutline className={classes.icone} />
                </InputAdornment>
              ),
            }}
          />

          <Typography className={classes.rotulo} style={{ marginTop: 18 }}>
            Senha
          </Typography>
          <TextField
            className={classes.input}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="password"
            name="password"
            placeholder="••••••••"
            value={user.password}
            onChange={handleChangeInput}
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            inputProps={{ onAnimationStart: handleAnimationStart }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined className={classes.icone} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="mostrar ou esconder a senha"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {erroLogin && (
            <div
              role="alert"
              style={{
                display: "flex", alignItems: "center", gap: 8, marginTop: 14,
                padding: "10px 14px", borderRadius: 12, fontSize: "0.88rem", fontWeight: 600,
                color: "#b42318", background: "#fef3f2", border: "1px solid #fecdca",
              }}
            >
              <span aria-hidden="true">⚠️</span>
              {erroLogin}
            </div>
          )}

          <Button type="submit" fullWidth variant="contained" className={classes.botao} disabled={entrando}>
            {entrando ? "Entrando..." : "Entrar"}
          </Button>

          {allowSignup && (
            <Typography className={classes.cadastro}>
              Não tem uma conta?{" "}
              <Link component={RouterLink} to="/cadastro" className={classes.cadastroLink}>
                Cadastre-se
              </Link>
            </Typography>
          )}
        </form>
      </Grid>
    </Grid>
  );
};

export default Login;
